/** Angular */
import { Injectable } from '@angular/core';
/** Rxjs */
import { Observable } from 'rxjs';
/** Models */
import { DsProject } from '../models/ds-project';
import { BydModel } from '@sixense/byd-core-service';
/** Services */
import { BydRhsAbstractService } from '../../../shared/services/byd-rhs-abstract.service';
import { BydGenerateParams } from '../../../functions/byd-generate-params.function';

@Injectable({
  providedIn: 'root',
})
export class DsProjectService extends BydRhsAbstractService {
  /**
   * Implement inherited abstract member 'path' from BydRhsAbstractService
   */
  public path: string;

  /**
   * Get a List of projects.
   *
   * @param status list of status ids.
   * @returns : Observable<BydModel<DsProject>>
   */
  public getProjects(status?: number[]): Observable<BydModel<DsProject>> {
    const params = BydGenerateParams({ status });
    this.path = '/projects';
    return this.getModels<DsProject>({ params });
  }

  /**
   * Get projects not linked to any organisation.
   *
   * @param organisationId id of the organisation
   * @returns Observable<BydModel<DsProject>>
   */
  public getProjectsNotLinked(): Observable<BydModel<DsProject>> {
    this.path = `/organisations/projects`;
    return this.getModels<DsProject>();
  }

  /**
   * Get projects linked to parent organisation.
   *
   * @param organisationId - Id of the organisation
   * @param projectId - Id of the project
   * @param status list of project status to get.
   * @returns Observable<BydModel<DsProject>>
   */
  public getProjectsLinksParentOrganisation(
    organisationId: number,
    status?: number[]
  ): Observable<BydModel<DsProject>> {
    const params = BydGenerateParams({ status });
    this.path = `/organisations/parent/${organisationId}/projects`;
    return this.getModels<DsProject>({ params });
  }

  /**
   * Get a single project.
   *
   * @param projectId Id of the project
   * @returns Observable<DsProject>
   */
  public getProject(projectId: number): Observable<DsProject> {
    this.path = `/projects/${projectId}`;
    return this.getModel<DsProject>();
  }

  /**
   * Update an existing project.
   *
   * @param dsProject Object of the project to update
   * @returns Observable<DsProject>
   */
  public postProject(dsProject: DsProject): Observable<DsProject> {
    this.path = `/projects/${dsProject.g_id}`;
    return this.putModel<DsProject>({ body: dsProject });
  }

  /**
   * Archive a project.
   *
   * @param projectId project id
   * @returns Observable<DsProject>
   */
  public archiveProject(projectId: number): Observable<boolean> {
    this.path = `/dsproject/v1/projects/${projectId}/archive`;
    return this.put({
      body: {
        archived: true,
      },
    });
  }

  /**
   * Update a single field of project.
   *
   * @param projectId Id of the project
   * @param column Update column of project
   * @param value Set new value
   * @returns Observable type `<boolean>`
   */
  public patchProject(projectId: number, column: string, value: string): Observable<boolean> {
    this.path = `/projects/${projectId}`;
    return this.patch<boolean>({ body: { column, value } });
  }

  /**
   * Get the project logo.
   *
   * @param projectId the project id
   * @returns Observable<Blob>
   */
  getLogoByProject(projectId: number): Observable<Blob> {
    this.path = `/projects/${projectId}/logo`;
    return this.get({ responseType: 'blob' });
  }

  /**
   * Add project logo.
   *
   * @param projectId the project id
   * @param file the logo file
   * @returns Observable<Blob>
   */
  addLogoToProject(projectId: number, file: File): Observable<Blob> {
    this.path = `/projects/${projectId}/logo`;
    const body = new FormData();
    const headers = this.bydConfigService.headers.delete('Content-Type');
    body.append('logo', file, file.name);
    return this.post({ body, responseType: 'blob', headers });
  }

  /**
   * Create DsProject instance.
   *
   * @param  jsonObject The flat json object.
   * @returns an `DsProject` instance.
   */
  // TODO:  Set up the correct type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public createNewInstance(jsonObject: any): DsProject {
    return new DsProject(jsonObject);
  }
}
